import { fetchJSON, withTimeout } from '../utils/network';

const isBrowser = typeof window !== 'undefined';

// Corrupt or unavailable storage must not crash the whole application.
const readStoredObject = (key) => {
    if (!isBrowser) return {};
    try {
        const value = JSON.parse(window.localStorage.getItem(key));
        return value && typeof value === 'object' ? value : {};
    } catch (error) {
        return {};
    }
};

const getUser = () => readStoredObject('gatsbyUser');
const getStore = () => readStoredObject('gatsbyStore');
const getStoreLabel = () => readStoredObject('gatsbyStoreLabel');
const getStoreData = () => readStoredObject('gatsbyStoreData');
const setStoredObject = (key, value) =>
    window.localStorage.setItem(key, JSON.stringify(value));
const setStoreData = (data) => setStoredObject('gatsbyStoreData', data);

export const isLoggedIn = () => {
    if (!isBrowser) return false;

    const user = getUser();
    const store = getStore();

    return !!user.email && !!store.id;
};

export const getCurrentUser = () => getUser();

export const getCurrentStore = () => getStore();
export const getCurrentStoreLabel = () => getStoreLabel();

export const getCurrentStoreData = () => getStoreData();

export const updateStoreData = async (storeId) => {
    if (!isBrowser) return null;

    console.log('[DEBUG] updateStoreData - début avec storeId:', storeId);

    // Si storeId est null ou undefined, utiliser les données en cache
    if (!storeId) {
        console.error('[DEBUG] ID du magasin non défini');
        return getStoreData();
    }

    try {
        // Attempt the actual service; navigator.onLine must never block it.
        const stores = await fetchJSON(
            'https://raw.githubusercontent.com/JimmyBeldone/brico-shared/main/listWithLabel.json',
        );

        console.log(
            '[DEBUG] Données récupérées, nombre de magasins:',
            stores.length,
        );

        // Conversion en chaîne pour la comparaison
        const storeIdStr = String(storeId).trim();
        const storeIdNum = parseInt(storeIdStr, 10);

        console.log(
            '[DEBUG] Recherche du magasin avec ID:',
            storeIdStr,
            'ou',
            storeIdNum,
        );

        // Recherche plus robuste avec multiple formats d'ID
        const storeData = stores.find(
            (store) =>
                String(store.id) === storeIdStr ||
                store.id === storeIdNum ||
                String(store.legacy) === storeIdStr ||
                store.legacy === storeIdNum ||
                String(store.easier) === storeIdStr ||
                store.easier === storeIdNum,
        );

        console.log(
            '[DEBUG] Résultat de la recherche du magasin:',
            storeData || 'Aucun magasin trouvé',
        );

        if (storeData) {
            console.log(
                '[DEBUG] Sauvegarde des données du magasin dans localStorage',
            );
            // Ignore a response from a previous depot after logout/switch.
            if (String(getStore().id) !== String(storeId)) return null;
            setStoreData(storeData);
            try {
                window.localStorage.setItem(
                    'brico_store_data_last_update',
                    Date.now().toString(),
                );
            } catch (error) {
                // Local data is already saved; the timestamp is optional.
            }
            return storeData;
        }

        // Si aucun magasin trouvé mais des données en cache, utiliser le cache
        const cachedData = getStoreData();
        if (cachedData && Object.keys(cachedData).length > 0) {
            console.log(
                '[DEBUG] Aucun magasin trouvé dans la liste mais données en cache disponibles',
            );
            return cachedData;
        }

        console.error("[DEBUG] Aucun magasin trouvé avec l'ID:", storeId);
        return null;
    } catch (error) {
        console.error('[DEBUG] Error fetching store data:', error);
        // En cas d'erreur, retourner les données en cache si elles existent
        return getStoreData();
    }
};

export const handleLogin = async (user, id, label, storeData) => {
    if (!isBrowser) return false;

    // Persist depot data already obtained by the login form. A second network
    // request must not hold up access or leave the first offline start empty.
    if (storeData) setStoreData(storeData);
    setStoredObject('gatsbyStoreLabel', { label });
    setStoredObject('gatsbyStore', { id });
    setStoredObject('gatsbyUser', user);
    window.localStorage.removeItem('brico_store_data_last_update');
    window.localStorage.removeItem('brico_home_sync_timestamp');
    return user;
};

/**
 * Synchronise les données du magasin entre le localStorage et Firebase
 * @param {Object} firebase - Instance Firebase
 * @param {number|string} storeId - ID du magasin
 * @param {Object} storeData - Données complètes du magasin
 * @returns {Promise<Object>} - Données mises à jour
 */
export const synchronizeStoreData = async (
    firebase,
    storeId,
    storeData = null,
) => {
    if (!isBrowser || !firebase || !storeId) return null;

    try {
        // Si on n'a pas les données complètes du magasin, essayer de les récupérer
        let completeStoreData = storeData;
        if (!completeStoreData) {
            completeStoreData = getStoreData();

            // Si pas de données dans le localStorage et connexion internet, les récupérer
            if (
                !completeStoreData ||
                Object.keys(completeStoreData).length === 0
            ) {
                completeStoreData = await updateStoreData(storeId);
            }
        }

        // A connectivity hint cannot establish Firebase availability.
        if (completeStoreData && completeStoreData.id) {
            const storeRef = firebase.database().ref(`/stores/${storeId}`);
            const snapshot = await withTimeout(storeRef.get());

            if (snapshot.exists()) {
                // Mise à jour des données existantes
                await withTimeout(
                    storeRef.transaction((item) => {
                        if (item) {
                            return {
                                ...item,
                                // eslint-disable-next-line no-underscore-dangle
                                appVersion: process.env.__VERSION__,
                                lastTimeActive: new Date().toISOString(),
                                legacy:
                                    completeStoreData?.legacy || item.legacy,
                                easier:
                                    completeStoreData?.easier || item.easier,
                                region:
                                    completeStoreData?.region || item.region,
                                bassin:
                                    completeStoreData?.bassin || item.bassin,
                            };
                        }
                        return item;
                    }),
                );
            } else if (completeStoreData) {
                // Création d'une nouvelle entrée
                await withTimeout(
                    storeRef.set({
                        // eslint-disable-next-line no-underscore-dangle
                        appVersion: process.env.__VERSION__,
                        id: completeStoreData.id,
                        label:
                            completeStoreData.label || getStoreLabel()?.label,
                        lastTimeActive: new Date().toISOString(),
                        legacy: completeStoreData.legacy,
                        easier: completeStoreData.easier,
                        region: completeStoreData.region,
                        bassin: completeStoreData.bassin,
                    }),
                );
            }
            return completeStoreData;
        }

        return null;
    } catch (error) {
        console.error('Error synchronizing store data:', error);
        return null;
    }
};

export const logOut = () => {
    if (!isBrowser) return;

    // Nettoyer le localStorage
    window.localStorage.removeItem('gatsbyUser');
    window.localStorage.removeItem('gatsbyStore');
    window.localStorage.removeItem('gatsbyStoreLabel');
    window.localStorage.removeItem('gatsbyStoreData');
    window.localStorage.removeItem('brico_store_data_last_update');
    window.localStorage.removeItem('brico_home_sync_timestamp');
};
